import { NotificationContext } from "@app/notifications/NotificationContext";
import { useContext, useEffect, useRef } from "react";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import { Message, MessageType } from "@app/notifications";
import { ProcessingSpecialtyGroupExceptionsPayload, ZipCodeAnalysisStatusChangeNotificationMessagePayload, ZipCodeAnalysisNcpStatusChangeNotificationMessagePayload } from "@app/notifications/message";
import { ProjectRunConfigurationStatus, ProjectRunStatusEnum } from "@app/projects/enumerators";
import { ZipProjectActionsEnum } from "@app/projects/zip-projects/interfaces";
import { isProjectRunning } from "@app/projects/zip-projects/utility";
import ProjectService from "@app/projects/projectService";
import { ChangeProjectStatusAction } from "./interfaces/actions/changeProjectStatusAction";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

export const ZipProjectNotificationsContainer = (): void => {
  const notificationsContext = useContext(NotificationContext);
  const projectContext = useContext(ZipProjectContext);
  const user = useContext(UserContext);
  const projectService = useRef(new ProjectService()).current;

  useEffect(() => {
    notificationsContext.subscribe(onNotificationReceived);

    return () => {
      notificationsContext.unsubscribe(onNotificationReceived);
    }
  });

  const onNotificationReceived = (message: Message) => {
    switch (message.notificationType) {
      // The curly braces on each case allow const payload to be scoped to the case instead of the switch
      case MessageType.ZipCodeAnalysisStatusChangeNotification: {
        const payload = message.payload as ZipCodeAnalysisStatusChangeNotificationMessagePayload;
        if (payload.projectId === projectContext.state.project.projectId) {
          const updatedConfiguration = projectContext.state.project.projectRunConfigurations?.find((prc) => prc.projectRunConfigurationId === payload.projectRunConfigurationId);
          // if it's a new run configuration, or if the new status is succeeded
          if (!updatedConfiguration || payload.projectRunStatus === ProjectRunStatusEnum.Succeeded) {
            projectContext.onChangeProjectStatus(payload);

            // else if it's NOT a new configuration
          } else if (!!updatedConfiguration) {
            const propertiesThatAreNotChangingInThisCase: Partial<ChangeProjectStatusAction> = {
              calculationMethod: projectContext.state.calculationMethod,
              calculationMethodCompared: projectContext.state.calculationMethodCompared,
              filterOptions: projectContext.state.filterOptions,
              filterSelections: projectContext.state.filterSelections,
              filterSelectionsTemp: projectContext.state.filterSelectionsTemp,
              hasExpandableSpecialties: projectContext.state.hasExpandableSpecialties,
              isDrilledIn: projectContext.state.isDrilledIn,
              regionSetIds: projectContext.state.regionSetIds,
              regionSetSpecialtyGroupToPartsLookup: projectContext.state.regionSetSpecialtyGroupToPartsLookup,
              selectedRows: projectContext.state.selectedRows,
              skip: projectContext.state.skip,
              specialtyExceptionsMap: projectContext.state.specialtyExceptionsMap,
              templateMetadata: projectContext.state.templateMetadata,
              totalNumberOfRecords: projectContext.state.totalNumberOfRecords,
              trendChartData: projectContext.state.trendChartData,
              urlSearchString: projectContext.state.urlSearchString,
              zipDetails: projectContext.state.zipDetails,
              zipDetailsCompared: projectContext.state.zipDetailsCompared,
              zipExpansions: projectContext.state.zipExpansions,
              zipExpansionsCompared: projectContext.state.zipExpansionsCompared,
            };

            updatedConfiguration.status = payload.projectRunStatus;
            const newIsRunning = isProjectRunning(projectContext.state.project);
            projectContext.dispatch({
              payload: {
                ...propertiesThatAreNotChangingInThisCase as ChangeProjectStatusAction,
                loading: false,
                project: {
                  ...projectContext.state.project,
                  isReadOnly: newIsRunning,
                  isRunning: newIsRunning,
                },
                runConfiguration: updatedConfiguration,
              },
              type: ZipProjectActionsEnum.ChangeProjectStatus,
            })
          }
        }
        break;
      }
      case MessageType.ProcessingSpecialtyGroupExceptions: {
        const payload = message.payload as ProcessingSpecialtyGroupExceptionsPayload;
        const newProject = structuredClone(projectContext.state.project);
        let newRunConfiguration = projectContext.state.runConfiguration;
        // if the notification is targeting this project and the user is allowed to see it
        if (projectContext.state.project?.projectId === payload.projectId
          && (!projectContext.state.project.resource.isShared
            || user.roles.includes(Roles.PREVIEW)
            || payload.isPublished
          )
        ) {
          newProject.hasUnprocessedSpecialtyGroupExceptions = false;
          switch (payload.status) {
            // if the payload status is succeeded
            case ProjectRunConfigurationStatus.Succeeded:
              // and if the targeted run config is the latest published or unpublished
              if (payload.projectRunConfigurationId === projectService.maxRunConfigurationId(projectContext.state.project, projectContext.state.runConfiguration.projectRunConfigurationType)) {
                // change to the targeted run config
                newRunConfiguration = projectContext.state.project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === payload.projectRunConfigurationId);
              }

              newProject.isReadOnly = payload.isReadOnly;
              newProject.isEditRestricted = payload.isEditRestricted;
              newProject.isRunning = false;

              newRunConfiguration.status = payload.status;

              projectContext.onFinishProcessingException(newProject, newRunConfiguration, true);
              break;
            // but if the payload status is processing
            case ProjectRunConfigurationStatus.ProcessingSpecialtyGroupExceptionAnalysis:
            case ProjectRunConfigurationStatus.QueuedSpecialtyGroupExceptionAnalysis:
              projectContext.onProcessSpecialtyExceptions(newProject, newRunConfiguration, payload);
              break;
            default:
              break;
          }
        }

        break;
      }
      case MessageType.ZipCodeAnalysisNcpStatusChange: {
        const payload = message.payload as ZipCodeAnalysisNcpStatusChangeNotificationMessagePayload;
        if (projectContext.state.runConfiguration.projectRunConfigurationId === payload.projectRunConfigurationId) {
          projectContext.onNcpProjectRunStatusChange(payload);
        }
        break;
      }
    }
  }
}
